import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';
import styles from './index.module.scss'; 

interface AudioPlayerProps {
    src?: string | Blob;
    className?: string;
    onPlayStateChange?: (isPlaying: boolean) => void;
    onVolumeChange?: (volume: number) => void;
    onDurationChange?: (duration: number) => void;
}

interface WaveformData {
    bars: number[];
    isLoading: boolean;
    error?: string;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({
    src,
    className = '',
    onPlayStateChange,
    onVolumeChange,
    onDurationChange
}) => {
    const [isPlaying, setIsPlaying] = useState<boolean>(false);
    const [currentTime, setCurrentTime] = useState<number>(0);
    const [duration, setDuration] = useState<number>(0);
    const [playbackRate, setPlaybackRate] = useState<number>(1);
    const [volume, setVolume] = useState<number>(1.0);
    const [isMuted, setIsMuted] = useState<boolean>(false);
    const [audioUrl, setAudioUrl] = useState<string | null>(null); 
    const [currentSrcLoaded, setCurrentSrcLoaded] = useState<string | Blob | null>(null);
    const [waveformData, setWaveformData] = useState<WaveformData>({
        bars: [],
        isLoading: false
    });

    const audioRef = useRef<HTMLAudioElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const abortControllerRef = useRef<AbortController | null>(null); // 用于取消 fetch 请求

    const playbackRates: number[] = [0.5, 0.75, 1, 1.25, 1.5, 2];

    const isDisabled = !audioUrl; // 只依赖 audioUrl 是否存在，因为 src 可能是 blob

    const generateWaveform = useCallback((audioBuffer?: AudioBuffer): number[] => {
        const samples = 80;
        const data: number[] = [];

        if (audioBuffer) {
            const channelData = audioBuffer.getChannelData(0);
            const blockSize = Math.floor(channelData.length / samples);

            for (let i = 0; i < samples; i++) {
                let sum = 0;
                for (let j = 0; j < blockSize; j++) {
                    sum += Math.abs(channelData[i * blockSize + j]);
                }
                data.push(sum / blockSize);
            }
        } else {
            // 生成演示波形数据
            for (let i = 0; i < samples; i++) {
                const baseHeight = Math.sin(i * 0.1) * 0.3 + 0.5;
                const noise = (Math.random() - 0.5) * 0.4;
                const envelope = Math.sin((i / samples) * Math.PI);
                data.push(Math.max(0.15, Math.min(1, baseHeight + noise * envelope)));
            }
        }
        return data;
    }, []);

    // 这是一个通用的加载音频数据的函数，接受 Blob 或 File 对象
    const processAudioData = useCallback(async (data: Blob | File, originalIdentifier: string | Blob | null = null) => {
        // 清理之前的URL
        if (audioUrl) {
            URL.revokeObjectURL(audioUrl);
        }
        // 如果有正在进行的 fetch 请求，取消它
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }

        const url = URL.createObjectURL(data);
        setAudioUrl(url); // 更新 audio 元素的 src
        setIsPlaying(false);
        setCurrentTime(0);
        setDuration(0);
        setCurrentSrcLoaded(originalIdentifier); // 标记当前成功加载的 src

        // 确保 audio 元素加载新源
        if (audioRef.current) {
            audioRef.current.load(); // 显式调用 load()
        }

        setWaveformData({ bars: [], isLoading: true });

        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

        try {
            const arrayBuffer = await data.arrayBuffer();
            const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
            const waveform = generateWaveform(audioBuffer);
            setWaveformData({ bars: waveform, isLoading: false });
        } catch (error) {
            console.error('Error decoding audio:', error);
            setWaveformData({
                bars: generateWaveform(),
                isLoading: false,
                error: 'Failed to decode audio file or URL'
            });
            setCurrentSrcLoaded(null); // 加载失败，重置已加载状态
        }
    }, [audioUrl, generateWaveform]);


    const loadSrc = useCallback(async (source: string | Blob | undefined) => {
        if (!source) {
            // 如果 src 为空，则清理所有状态
            if (audioUrl) {
                URL.revokeObjectURL(audioUrl);
                setAudioUrl(null);
            }
            setCurrentSrcLoaded(null);
            setIsPlaying(false);
            setCurrentTime(0);
            setDuration(0);
            setWaveformData({ bars: generateWaveform(), isLoading: false });
            return;
        }

        // 检查 src 是否与当前已加载的源相同，避免不必要的重新加载
        // 对于 Blob，需要进行引用比较
        if (source === currentSrcLoaded) {
            return; // 源未改变，无需重新加载
        }

        if (source instanceof Blob) {
            // 如果是 Blob，直接处理
            await processAudioData(source, source); // 将 Blob 对象本身作为 originalIdentifier
        } else if (typeof source === 'string' && source.trim() !== '') {
            // 如果是字符串 URL，fetch 后处理
            if (abortControllerRef.current) {
                abortControllerRef.current.abort(); // 取消之前的 fetch 请求
            }

            abortControllerRef.current = new AbortController();
            const signal = abortControllerRef.current.signal;

            setWaveformData({ bars: [], isLoading: true }); // 显示加载中状态

            try {
                const response = await fetch(source, { signal });
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const blob = await response.blob();
                const fileName = source.substring(source.lastIndexOf('/') + 1) || 'audio.mp3';
                const file = new File([blob], fileName, { type: blob.type });
                await processAudioData(file, source); // 传递原始 URL 作为 originalIdentifier
            } catch (error) { 
                if (error instanceof Error) {
                    if (error.name === 'AbortError') {
                        console.log('Fetch aborted:', source);
                    } else {
                        console.error("Error loading audio from src:", error);
                        setWaveformData({
                            bars: generateWaveform(),
                            isLoading: false,
                            error: 'Failed to load audio from provided source'
                        });
                        setCurrentSrcLoaded(null); // 加载失败，重置已加载状态
                    }
                } else {
                    console.error("An unknown error occurred:", error);
                }
            } finally {
                abortControllerRef.current = null; // 清除 AbortController
            }
        }
    }, [processAudioData, generateWaveform, audioUrl, currentSrcLoaded]); // 依赖项调整

    // 当 src prop 变化时触发加载逻辑
    useEffect(() => {
        loadSrc(src); // 调用新的 loadSrc 函数
        // 清理函数：当组件卸载或 src 改变时，撤销旧的 Blob URL 并取消任何正在进行的 fetch
        return () => {
            if (audioUrl) {
                URL.revokeObjectURL(audioUrl);
            }
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
                abortControllerRef.current = null;
            }
        };
    }, [src, loadSrc, audioUrl]); // 依赖项调整

    // 设置音频音量和倍速
    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.volume = isMuted ? 0 : volume;
            audioRef.current.playbackRate = playbackRate;
        }
    }, [volume, isMuted, playbackRate]);

    // 音频加载完成
    const handleLoadedMetadata = useCallback((): void => {
        if (audioRef.current) {
            const audioDuration = audioRef.current.duration;
            setDuration(audioDuration);
            audioRef.current.volume = isMuted ? 0 : volume;
            audioRef.current.playbackRate = playbackRate;
            if (onDurationChange) {
                onDurationChange(audioDuration);
            }
        }
    }, [volume, isMuted, playbackRate, onDurationChange]);

    // 时间更新
    const handleTimeUpdate = useCallback((): void => {
        if (audioRef.current) {
            setCurrentTime(audioRef.current.currentTime);
        }
    }, []);

    // 播放状态变化
    const handlePlayStateChange = useCallback((playing: boolean): void => {
        setIsPlaying(playing);
        onPlayStateChange?.(playing);
    }, [onPlayStateChange]);

    // 播放/暂停
    const togglePlay = useCallback((): void => {
        if (!audioRef.current) return;

        if (isPlaying) {
            audioRef.current.pause();
            handlePlayStateChange(false);
        } else {
            audioRef.current.play().catch(console.error);
            handlePlayStateChange(true);
        }
    }, [isPlaying, handlePlayStateChange]);

    // 改变播放速度
    const changePlaybackRate = useCallback((): void => {
        const currentIndex = playbackRates.indexOf(playbackRate);
        const nextRate = playbackRates[(currentIndex + 1) % playbackRates.length];
        setPlaybackRate(nextRate);

        if (audioRef.current) {
            audioRef.current.playbackRate = nextRate;
        }
    }, [playbackRate, playbackRates]);

    // 音量控制
    const handleVolumeChange = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
        const newVolume = parseFloat(event.target.value);
        setVolume(newVolume);
        setIsMuted(newVolume === 0);
        onVolumeChange?.(newVolume);
    }, [onVolumeChange]);

    // 静音切换
    const toggleMute = useCallback((): void => {
        setIsMuted(!isMuted);
    }, [isMuted]);

    // 点击波形跳转
    const handleWaveformClick = useCallback((event: React.MouseEvent<HTMLDivElement>): void => {
        if (!duration || !audioRef.current) return;

        const rect = event.currentTarget.getBoundingClientRect();
        const clickX = event.clientX - rect.left;
        const percentage = clickX / rect.width;
        const newTime = percentage * duration;

        audioRef.current.currentTime = newTime;
    }, [duration]);

    // 格式化时间
    const formatTime = useCallback((time: number): string => {
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }, []);

    // 音频结束事件
    const handleAudioEnded = useCallback((): void => {
        handlePlayStateChange(false);
    }, [handlePlayStateChange]);

    return (
        <div>

            {/* 紧凑播放器 */}
            <div className={`${styles.compactAudioPlayer} ${className}`}>
                <div className={styles.content}>
                    {/* 波形显示 */}
                    <div className={styles.waveformContainer}>
                        {waveformData.isLoading ? (
                            <div className={styles.loadingState}>
                                🎵 加载中...
                            </div>
                        ) : waveformData.error ? (
                            <div className={styles.errorState}>
                                ⚠️ {waveformData.error}
                            </div>
                        ) : (
                            <div
                                className={styles.waveform}
                                onClick={handleWaveformClick}
                            >
                                {waveformData.bars.map((height: number, index: number) => {
                                    const progress = duration > 0 ? currentTime / duration : 0;
                                    const isActive = index < progress * waveformData.bars.length;

                                    return (
                                        <div
                                            key={index}
                                            className={`${styles.waveformBar} ${isActive ? styles.active : styles.inactive
                                                } ${isPlaying && isActive ? styles.playing : ''}`}
                                            style={{
                                                height: `${Math.max(height * 40, 4)}px`
                                            }}
                                        />
                                    );
                                })}
                            </div>
                        )}

                        {/* 时间标记 */}
                        <div className={styles.timeLabels}>
                            <span>{formatTime(currentTime)}</span>
                            <span>{formatTime(duration)}</span>
                        </div>
                    </div>

                    {/* 底部控制 */}
                    <div className={styles.bottomControls}>
                        <div className={styles.leftControls}>
                            <button
                                onClick={togglePlay}
                                className={styles.playBtn}
                                disabled={isDisabled}
                                title={isPlaying ? "暂停" : "播放"}
                            >
                                {isPlaying ? <Pause size={16} /> : <Play size={16} />}
                            </button>

                            <button
                                onClick={changePlaybackRate}
                                className={styles.playbackRate}
                                title="播放速度"
                                disabled={isDisabled}
                            >
                                {playbackRate}x
                            </button>
                        </div>

                        <div className={styles.volumeControl}>
                            <button
                                onClick={toggleMute}
                                className={styles.volumeBtn}
                                title={isMuted ? "取消静音" : "静音"}
                                disabled={isDisabled}
                            >
                                {isMuted || volume === 0 ? <VolumeX size={14} /> : <Volume2 size={14} />}
                            </button>

                            <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.01"
                                value={isMuted ? 0 : volume}
                                onChange={handleVolumeChange}
                                className={styles.volumeSlider}
                                title={`音量: ${Math.round((isMuted ? 0 : volume) * 100)}%`}
                                disabled={isDisabled}
                            />
                        </div>
                    </div>

                    {/* 音频元素 */}
                    {audioUrl && (
                        <audio
                            ref={audioRef}
                            src={audioUrl}
                            onLoadedMetadata={handleLoadedMetadata}
                            onTimeUpdate={handleTimeUpdate}
                            onEnded={handleAudioEnded}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default AudioPlayer;